/**
 * Client tasks API (ENG-2845) — the browser half of the general tasks system
 * (backend: valur/tasks, ENG-2844).
 *
 * Every endpoint is flag-gated server-side by `client-tasks`: a 403 means the
 * flag is off for this user, which callers treat as "no tasks", not an error.
 * Task identity in routes is the task's uuid; `answers` is an object keyed by
 * step id and is MERGED server-side, never replaced.
 */
import axios from 'axios';

import store from '@/store';
import { API_ROOT } from '@/store/api';

const base = userId => `${API_ROOT}/${userId}/tasks`;

export async function fetchTasks(userId) {
    const { data } = await axios.get(`${base(userId)}/`, store.getters.axiosConfig);
    return data;
}

/**
 * Staff-only manage view (admin client profile): every audience and status
 * for the client, optionally narrowed to one project (intake uuid).
 */
export async function fetchManagedTasks(userId, { intake = null } = {}) {
    const config = {
        ...store.getters.axiosConfig,
        params: { scope: 'manage', ...(intake ? { intake } : {}) },
    };
    const { data } = await axios.get(`${base(userId)}/`, config);
    return data;
}

/** Staff-only manual creation — the API twin of Area 51's manual add. */
export async function createTask(userId, { definitionKey, intake = null, dueDate = null }) {
    const { data } = await axios.post(
        `${base(userId)}/`,
        {
            definition_key: definitionKey,
            ...(intake ? { intake } : {}),
            ...(dueDate ? { due_date: dueDate } : {}),
        },
        store.getters.axiosConfig,
    );
    return data;
}

/**
 * Staff-only transitions from the admin task drawer — the API twin of the
 * Area 51 actions: {action: complete|dismiss|reopen|set_due_date, reason?, due_date?}.
 */
export async function manageTask(userId, taskUuid, payload) {
    const { data } = await axios.post(`${base(userId)}/${taskUuid}/manage/`, payload, store.getters.axiosConfig);
    return data;
}

/** Staff-only registry catalog, incl. each scheduled definition's next window. */
export async function fetchTaskDefinitions(userId) {
    const { data } = await axios.get(`${base(userId)}/definitions/`, store.getters.axiosConfig);
    return data;
}

export async function saveTaskAnswers(userId, taskUuid, answers) {
    const { data } = await axios.patch(`${base(userId)}/${taskUuid}/`, { answers }, store.getters.axiosConfig);
    return data;
}

export async function reopenTask(userId, taskUuid) {
    const { data } = await axios.post(`${base(userId)}/${taskUuid}/reopen/`, {}, store.getters.axiosConfig);
    return data;
}

export async function submitTask(userId, taskUuid, answers = null) {
    const { data } = await axios.post(
        `${base(userId)}/${taskUuid}/submit/`,
        answers ? { answers } : {},
        store.getters.axiosConfig,
    );
    return data;
}

/**
 * Attach an already-uploaded file (see services/upload.js with
 * `createAssociation: false`) to a task's upload step.
 */
export async function attachTaskDocument(userId, taskUuid, { documentId, documentType, displayName }) {
    const { data } = await axios.post(
        `${base(userId)}/${taskUuid}/documents/`,
        {
            document: documentId,
            document_type: documentType,
            display_name: displayName || '',
        },
        store.getters.axiosConfig,
    );
    return data;
}

export async function removeTaskDocument(userId, taskUuid, documentId) {
    await axios.delete(`${base(userId)}/${taskUuid}/documents/${documentId}/`, store.getters.axiosConfig);
}
